import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, finalize, forkJoin, map, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import {
    CropAreaPlantationReportRowViewModel,
    CropAreaPlantationReportRowViewModelListResponseResult,
    CropMapLocationCountryResponse,
    CropMapPlantationProxy,
    GetCropAreaPlantationReportQuery,
} from '../../proxy/proxies';
import { AppConfigService } from '../../core/services/app-config.service';
import { UserService } from '../../core/services/user.service';
import { CityBoundaryLoaderService } from '../../core/map/services/city-boundary-loader.service';
import { DistrictBoundaryLoaderService } from '../../core/map/services/district-boundary-loader.service';
import {
    FieldBoundaryCollection,
    FieldBoundaryDecoderService,
} from '../../core/map/services/field-boundary-decoder.service';
import { FieldBoundaryLoadService } from '../../core/map/services/field-boundary-load.service';
import { usesStringGeoIds } from '../../core/map/services/field-unit-properties.util';
import {
    fieldTileForZoom,
    fieldTilesEqual,
    FieldTile,
    FieldUnitType,
} from '../../core/map/map.service';
import type {
    CityBoundariesPayload,
    CityOption,
    DistrictBoundariesPayload,
    DistrictOption,
    MapLocationSelection,
    PlantationCropOption,
    PlantationParams,
    SeasonCropPathKeys,
} from './plantation.models';

export type {
    CityBoundariesPayload,
    CityOption,
    DistrictBoundariesPayload,
    DistrictOption,
    MapLocationSelection,
    PlantationCropOption,
    PlantationParams,
    RegionStateOption,
    SeasonCropPathKeys,
} from './plantation.models';

@Injectable({ providedIn: 'root' })
export class PlantationService {
    private readonly cropMapPlantationProxy = inject(CropMapPlantationProxy);
    private readonly http = inject(HttpClient);
    private readonly appConfigService = inject(AppConfigService);
    private readonly cityBoundaryLoader = inject(CityBoundaryLoaderService);
    private readonly districtBoundaryLoader = inject(DistrictBoundaryLoaderService);
    private readonly fieldBoundaryDecoder = inject(FieldBoundaryDecoderService);
    private readonly fieldBoundaryLoadService = inject(FieldBoundaryLoadService);
    private readonly userService = inject(UserService);

    params: PlantationParams | null = null;
    private paramsSubject = new BehaviorSubject<PlantationParams | null>(null);
    params$ = this.paramsSubject.asObservable();

    private dataSubject = new Subject<CropAreaPlantationReportRowViewModel[]>();
    data$ = this.dataSubject.asObservable();

    private loadingSubject = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject.asObservable();

    private mapLocationSelectionSubject = new Subject<MapLocationSelection>();
    mapLocationSelection$ = this.mapLocationSelectionSubject.asObservable();

    highlightedCity: CityOption | null = null;
    private highlightedCitySubject = new BehaviorSubject<CityOption | null>(null);
    highlightedCity$ = this.highlightedCitySubject.asObservable();

    private panelOpenSubject = new BehaviorSubject<boolean>(true);
    panelOpen$ = this.panelOpenSubject.asObservable();

    private cityBoundariesSubject = new BehaviorSubject<CityBoundariesPayload | null>(null);
    cityBoundaries$ = this.cityBoundariesSubject.asObservable();

    private cityBoundariesLoadingSubject = new BehaviorSubject<boolean>(false);
    cityBoundariesLoading$ = this.cityBoundariesLoadingSubject.asObservable();

    private districtBoundariesSubject = new BehaviorSubject<DistrictBoundariesPayload | null>(null);
    districtBoundaries$ = this.districtBoundariesSubject.asObservable();

    private districtBoundariesLoadingSubject = new BehaviorSubject<boolean>(false);
    districtBoundariesLoading$ = this.districtBoundariesLoadingSubject.asObservable();

    private fieldBoundariesSubject = new BehaviorSubject<FieldBoundaryCollection | null>(null);
    fieldBoundaries$ = this.fieldBoundariesSubject.asObservable();

    private fieldBoundariesLoadingSubject = new BehaviorSubject<boolean>(false);
    fieldBoundariesLoading$ = this.fieldBoundariesLoadingSubject.asObservable();

    isFieldBoundariesLoading(): boolean {
        return this.fieldBoundariesLoadingSubject.getValue();
    }

    /** district access key (blob unit key) → numeric district_id on field features */
    private districtNumericIdByAccessKey = new Map<string, number>();
    private currentCountryId: number | null = null;
    private cancelCityBoundaryFetch$ = new Subject<void>();
    private cancelDistrictBoundaryFetch$ = new Subject<void>();
    private cancelFieldBoundaryFetch$ = new Subject<void>();

    /** When set, field layer filters to districts in this city (map drill-down). */
    private mapScopedCityId: string | null = null;
    /** When set, field blobs and report are scoped to one district (map drill-down). */
    private mapScopedDistrictAccessKey: string | null = null;
    /** Full city + districts from params at city drill (supports switching district after narrow). */
    private mapDrillSourceCity: CityOption | null = null;
    /** Filter `districtId` → blob `district_access_key` captured at city drill. */
    private mapDrillDistrictBlobKeys = new Map<string, string>();
    /** Last filter Apply snapshot; map clicks resolve cities against this, not narrowed params. */
    private mapDrillBaseParams: PlantationParams | null = null;
    /** Cities intersecting the map viewport when not drilled into a single city. */
    private fieldViewportCityIds: string[] = [];
    /** Scope used with {@link loadedFieldTile} to avoid redundant district fetches. */
    private loadedFieldScopeKey: string | null = null;

    private reportRows: CropAreaPlantationReportRowViewModel[] = [];
    /** Map zoom from the plantation map (initial matches MapService default). */
    private fieldMapZoom = 1;
    /** Tile whose field blobs are loaded; null until a fetch completes guards. */
    private loadedFieldTile: FieldTile | null = null;

    changePlantationParams(params: PlantationParams): Observable<CropAreaPlantationReportRowViewModelListResponseResult> {
        this.dataSubject.next([]);
        this.clearFieldBoundaries();
        this.clearDistrictBoundaries();
        this.mapScopedCityId = null;
        this.mapScopedDistrictAccessKey = null;
        this.mapDrillSourceCity = null;
        this.mapDrillDistrictBlobKeys.clear();
        this.fieldViewportCityIds = [];
        this.loadedFieldScopeKey = null;

        this.params = params;
        this.mapDrillBaseParams = structuredClone(params);
        this.paramsSubject.next(params);

        const newCountryId = params.country?.countryId ?? null;
        const authorizedCityIds = params.cities.map((c) => c.cityId);
        if (newCountryId !== this.currentCountryId) {
            this.currentCountryId = newCountryId;
            this.loadCityBoundariesForCountry(params.country, authorizedCityIds);
        }

        if (params.cities?.length > 0 && params.cities?.length <= 3) {
            this.onChangeHighlightedCity(params.cities[0]);
        } else {
            this.onChangeHighlightedCity(null);
        }
        this.highlightedCitySubject.next(this.highlightedCity);

        return this.fetchPlantationReport(params);
    }

    /** Map district click: narrow location, sync filter, reload report without resetting boundary layers. */
    applyLocationScopeFromMap(params: PlantationParams): Observable<CropAreaPlantationReportRowViewModelListResponseResult> {
        this.dataSubject.next([]);
        this.params = params;
        this.paramsSubject.next(params);
        return this.fetchPlantationReport(params);
    }

    private fetchPlantationReport(
        params: PlantationParams,
    ): Observable<CropAreaPlantationReportRowViewModelListResponseResult> {
        const payload = new GetCropAreaPlantationReportQuery();
        payload.enterpriseId = this.userService.selectedEnterpriseId;
        payload.seasons = [params.season.id];
        payload.crops = params.crops.map((c) => c.id);
        payload.years = [params.year];
        payload.districts = params.cities.flatMap((c) => c.districts.map((d) => d.districtId));

        this.loadingSubject.next(true);
        return this.cropMapPlantationProxy
            .cropMapPlantation_CropMapPlantationReport(payload)
            .pipe(
                finalize(() => {
                    this.loadingSubject.next(false);
                }),
                tap({
                    next: (res: CropAreaPlantationReportRowViewModelListResponseResult) => {
                        if (res?.result?.length) {
                            const filteredResult = res.result!.filter(
                                (item) => item.totalArea != null && item.totalArea !== 0,
                            );
                            this.reportRows = filteredResult;
                            this.dataSubject.next(filteredResult);
                            this.maybeLoadDistrictBoundariesAfterMapReport();
                            this.reloadFieldBoundariesForCurrentZoom();
                        } else {
                            this.reportRows = [];
                            this.dataSubject.next([]);
                            this.clearFieldBoundaries();
                        }
                    },
                }),
            );
    }

    /** Records the map's current zoom; Apply and reload derive the field tile from this. */
    setFieldMapZoom(zoom: number): void {
        this.fieldMapZoom = zoom;
    }

    /** Updates viewport city scope from the map (district zoom only). */
    setFieldViewportCityIds(cityIds: string[]): void {
        this.fieldViewportCityIds = [...new Set(cityIds.map((id) => String(id).trim()).filter(Boolean))].sort();
    }

    /** City access key when the user drilled into one city on the map; null when using viewport scope. */
    getMapScopedCityId(): string | null {
        return this.mapScopedCityId;
    }

    getMapScopedDistrictAccessKey(): string | null {
        return this.mapScopedDistrictAccessKey;
    }

    /**
     * Map drill-down: load per-district field blobs even when map zoom is still country-tier.
     * Viewport-driven loads keep using {@link fieldTileForZoom} as-is.
     */
    private resolveFieldTileForLoad(): FieldTile {
        const tile = fieldTileForZoom(this.fieldMapZoom);
        if ((this.mapScopedCityId || this.mapScopedDistrictAccessKey) && tile.unitType === 'country') {
            return { unitType: 'district', fileZoom: 9 };
        }
        return tile;
    }

    /** Fetch field blobs when map zoom or viewport scope implies a different load than the one cached. */
    reloadFieldBoundariesForCurrentZoom(): void {
        if (!this.params || this.reportRows.length === 0) {
            return;
        }
        const tile = this.resolveFieldTileForLoad();
        const scopeKey = this.computeFieldScopeKey(tile);
        if (
            this.loadedFieldTile != null &&
            fieldTilesEqual(tile, this.loadedFieldTile) &&
            this.loadedFieldScopeKey === scopeKey
        ) {
            return;
        }
        this.loadFieldBoundaries(this.params, this.reportRows, tile);
    }

    private computeFieldScopeKey(tile: FieldTile): string {
        if (tile.unitType === 'country') {
            return 'country';
        }
        if (this.mapScopedDistrictAccessKey) {
            return `district:${this.mapScopedDistrictAccessKey}`;
        }
        if (this.mapScopedCityId) {
            return `city:${this.mapScopedCityId}`;
        }
        if (this.fieldViewportCityIds.length > 0) {
            return `viewport:${this.fieldViewportCityIds.join(',')}`;
        }
        return 'viewport:none';
    }

    onChangeHighlightedCity(city: CityOption | null): void {
        if (!city) {
            this.highlightedCity = null;
            this.highlightedCitySubject.next(null);
            return;
        }
        if (city && this.highlightedCity?.cityId !== city.cityId) {
            this.highlightedCity = city;
            this.highlightedCitySubject.next(this.highlightedCity);
        }
    }

    /** Map city click: narrow report/filter to city, load district boundaries, scope field filter. */
    onCitySelectedFromMap(cityAccessKey: string): CityOption | null {
        const city = this.findCityForMapDrill(cityAccessKey);
        if (!city) return null;

        const cityKey = cityAccessKey.trim();
        this.onChangeHighlightedCity(city);
        const scopedChanged = this.mapScopedCityId !== cityKey;
        this.mapScopedCityId = cityKey;
        this.mapScopedDistrictAccessKey = null;
        this.mapDrillSourceCity = structuredClone(city);
        this.setFieldViewportCityIds([cityKey]);
        if (scopedChanged) {
            this.loadedFieldScopeKey = null;
        }

        const narrowed = this.buildParamsForMapScope(city, city.districts);
        if (!narrowed) return null;

        this.mapLocationSelectionSubject.next({
            cityId: cityKey,
            districtIds: city.districts.map((d) => d.districtId).filter(Boolean),
        });

        this.applyLocationScopeFromMap(narrowed).subscribe();

        if (this.reportHasRowsForCity(cityKey)) {
            this.populateMapDrillDistrictBlobKeys(cityKey, city);
            this.loadDistrictBoundariesForCity(cityKey, city);
            if (this.params && this.reportRows.length > 0) {
                this.reloadFieldBoundariesForCurrentZoom();
            }
        } else {
            this.clearDistrictBoundaries();
        }

        return city;
    }

    findCityInParams(cityAccessKey: string): CityOption | null {
        if (!this.params) return null;
        return this.params.cities.find((c) => c.cityId === cityAccessKey) ?? null;
    }

    /** Resolves a city from the last Apply snapshot, then current params (map drill). */
    findCityForMapDrill(cityAccessKey: string): CityOption | null {
        const key = cityAccessKey?.trim();
        if (!key) return null;
        const fromBase = this.mapDrillBaseParams?.cities.find((c) => c.cityId === key);
        if (fromBase) {
            return structuredClone(fromBase);
        }
        return this.findCityInParams(key);
    }

    findDistrictInParams(cityAccessKey: string, districtAccessKey: string): DistrictOption | null {
        const cityKey = cityAccessKey?.trim();
        const normalizedKey = districtAccessKey?.trim();
        if (!cityKey || !normalizedKey) return null;

        const city =
            this.mapDrillSourceCity?.cityId === cityKey
                ? this.mapDrillSourceCity
                : this.findCityForMapDrill(cityKey);
        if (!city) return null;

        for (const district of city.districts) {
            const districtId = district.districtId?.trim();
            const blobKey =
                (districtId ? this.mapDrillDistrictBlobKeys.get(districtId) : undefined) ??
                this.resolveDistrictBlobAccessKey(cityKey, district);
            if (districtId === normalizedKey || blobKey === normalizedKey) {
                return district;
            }
        }
        return null;
    }

    /** Map district click: single-district report, blobs, and filter sync event. */
    onDistrictSelectedFromMap(cityAccessKey: string, districtAccessKey: string): boolean {
        if (!this.params) return false;
        const cityKey = cityAccessKey?.trim();
        const districtKey = districtAccessKey?.trim();
        if (!cityKey || !districtKey) return false;

        const district = this.findDistrictInParams(cityKey, districtKey);
        const city = this.findCityForMapDrill(cityKey);
        if (!district || !city) return false;

        const scopedChanged = this.mapScopedDistrictAccessKey !== districtKey;
        this.mapScopedCityId = cityKey;
        this.mapScopedDistrictAccessKey = districtKey;
        this.setFieldViewportCityIds([cityKey]);
        if (scopedChanged) {
            this.loadedFieldScopeKey = null;
        }

        const narrowed = this.buildParamsForMapScope(city, [district]);
        if (!narrowed) return false;

        this.mapLocationSelectionSubject.next({
            cityId: cityKey,
            districtIds: [district.districtId],
            districtId: district.districtId,
            districtAccessKey: districtKey,
        });

        this.applyLocationScopeFromMap(narrowed).subscribe();
        return true;
    }

    private buildParamsForMapScope(city: CityOption, districts: DistrictOption[]): PlantationParams | null {
        const sourceParams = this.mapDrillBaseParams ?? this.params;
        if (!sourceParams) return null;
        const narrowedCity: CityOption = {
            cityId: city.cityId,
            name: city.name,
            districts: districts.map((d) => ({ districtId: d.districtId, name: d.name })),
        };

        let regionOrStates = sourceParams.regionOrStates
            .filter((region) => region.cities.some((c) => c.cityId === city.cityId))
            .map((region) => ({
                ...region,
                cities: [narrowedCity],
            }));

        if (regionOrStates.length === 0) {
            regionOrStates = [
                {
                    id: city.cityId,
                    name: city.name,
                    kind: 'region' as const,
                    cities: [narrowedCity],
                },
            ];
        }

        return {
            ...sourceParams,
            cities: [narrowedCity],
            regionOrStates,
            isAllRegionsSelected: false,
        };
    }

    private reportHasRowsForCity(cityAccessKey: string): boolean {
        const key = cityAccessKey?.trim();
        if (!key) return false;
        return this.reportRows.some((r) => r.city?.accessKey?.trim() === key);
    }

    private populateMapDrillDistrictBlobKeys(cityAccessKey: string, city: CityOption): void {
        this.mapDrillDistrictBlobKeys.clear();
        for (const district of city.districts) {
            const blobKey =
                this.resolveDistrictBlobAccessKey(cityAccessKey, district) ?? district.districtId?.trim();
            if (blobKey && district.districtId) {
                this.mapDrillDistrictBlobKeys.set(district.districtId.trim(), blobKey);
            }
        }
    }

    /** After map-driven report reload, load district outlines when rows were missing at click time. */
    private maybeLoadDistrictBoundariesAfterMapReport(): void {
        const cityKey = this.mapScopedCityId?.trim();
        if (!cityKey || this.mapScopedDistrictAccessKey) {
            return;
        }
        if (!this.reportHasRowsForCity(cityKey)) {
            return;
        }
        const city = this.findCityInParams(cityKey) ?? this.findCityForMapDrill(cityKey);
        if (!city) {
            return;
        }
        this.populateMapDrillDistrictBlobKeys(cityKey, city);
        this.loadDistrictBoundariesForCity(cityKey, city);
    }

    /** First report row district access key for a city (debug when blob keys mismatch). */
    getSampleReportDistrictAccessKeyForCity(cityAccessKey: string): string | undefined {
        const key = cityAccessKey?.trim();
        if (!key) return undefined;
        const row = this.reportRows.find((r) => r.city?.accessKey?.trim() === key);
        return row?.district?.accessKey?.trim();
    }

    /** District blob access keys for a city (geographic `.bin` / report `district.accessKey`). */
    getDistrictAccessKeysForCity(cityAccessKey: string): string[] {
        const city = this.findCityInParams(cityAccessKey);
        if (!city) return [];
        return this.resolveDistrictBlobAccessKeys(city);
    }

    /** District access keys authorized for the map-scoped city (for district layer filter). */
    getDistrictAccessKeysForMapScopedCity(): string[] {
        if (!this.mapScopedCityId) return [];
        return this.getDistrictAccessKeysForCity(this.mapScopedCityId);
    }

    /**
     * Maps filter-tree districts to keys on decoded district boundary features.
     * Prefer report row `district.accessKey` (matches blobs); fallback to RoleAccess `districtId`.
     */
    resolveDistrictBlobAccessKeys(city: CityOption): string[] {
        const keys: string[] = [];
        const cityKey = city.cityId?.trim();
        if (!cityKey) return keys;

        for (const district of city.districts) {
            const fallback = district.districtId?.trim();
            if (!fallback) continue;

            const row = this.findReportRowForDistrict(cityKey, district);
            if (!row) {
                continue;
            }
            const blobKey = row.district?.accessKey?.trim();
            keys.push(blobKey || fallback);
        }
        return keys;
    }

    /**
     * Numeric district ids for field-layer filter — scoped to map city when drilled in,
     * otherwise all selected districts. Uses report rows to map access keys to numeric ids.
     */
    getFieldFilterDistrictIds(): number[] {
        if (this.loadedFieldTile?.unitType === 'country') {
            return [];
        }
        const numericIds = this.collectFieldFilterDistrictNumericIds();
        if (numericIds.length === 0) {
            return [];
        }
        if (numericIds.every((id) => id === 0)) {
            return [];
        }
        return numericIds;
    }

    /** District access keys for field-layer filter when features use string geo ids. */
    getFieldFilterDistrictAccessKeys(): string[] {
        if (this.loadedFieldTile?.unitType === 'country') {
            return [];
        }
        if (this.mapScopedDistrictAccessKey) {
            return [this.mapScopedDistrictAccessKey];
        }
        if (this.mapScopedCityId) {
            return this.getDistrictAccessKeysForCity(this.mapScopedCityId);
        }
        return this.getSelectedDistrictAccessKeys();
    }

    /** True when loaded field features carry GeographicUnitPayload unit metadata. */
    usesStringFieldGeoIds(): boolean {
        const sample = this.fieldBoundariesSubject.getValue()?.features[0]?.properties;
        return sample != null && usesStringGeoIds(sample);
    }

    /** Authorized district access keys from the current filter selection. */
    getSelectedDistrictAccessKeys(): string[] {
        if (!this.params) return [];
        const keys = new Set<string>();
        for (const city of this.params.cities) {
            for (const key of this.resolveDistrictBlobAccessKeys(city)) {
                keys.add(key);
            }
        }
        return [...keys];
    }

    private collectFieldFilterDistrictNumericIds(): number[] {
        if (this.mapScopedDistrictAccessKey) {
            const id = this.resolveDistrictNumericId(this.mapScopedDistrictAccessKey);
            return id != null ? [id] : [];
        }
        if (this.mapScopedCityId) {
            return this.getDistrictNumericIdsForCity(this.mapScopedCityId);
        }
        return this.getSelectedDistrictIds();
    }


    getDistrictNumericIdsForCity(cityAccessKey: string): number[] {
        if (!this.params) return [];
        const city = this.findCityInParams(cityAccessKey);
        if (!city) return [];

        const ids = new Set<number>();
        for (const d of city.districts) {
            const numericId = this.resolveDistrictNumericIdForDistrict(cityAccessKey, d);
            if (numericId != null) ids.add(numericId);
        }
        return [...ids];
    }

    private findReportRowForDistrict(
        cityAccessKey: string,
        district: DistrictOption,
    ): CropAreaPlantationReportRowViewModel | undefined {
        const districtName = district.name?.trim().toLowerCase();
        const districtId = district.districtId?.trim();

        for (const row of this.reportRows) {
            const rowCityKey = row.city?.accessKey?.trim();
            if (rowCityKey !== cityAccessKey) continue;

            const rowDistrictKey = row.district?.accessKey?.trim();
            if (districtId && rowDistrictKey === districtId) {
                return row;
            }
            const rowDistrictName = row.district?.name?.trim().toLowerCase();
            if (districtName && rowDistrictName === districtName) {
                return row;
            }
        }
        return undefined;
    }

    private resolveDistrictNumericIdForDistrict(
        cityAccessKey: string,
        district: DistrictOption,
    ): number | null {
        const blobKey = this.resolveDistrictBlobAccessKey(cityAccessKey, district);
        if (!blobKey) return null;
        return this.resolveDistrictNumericId(blobKey);
    }

    private resolveDistrictBlobAccessKey(cityAccessKey: string, district: DistrictOption): string | undefined {
        const fallback = district.districtId?.trim();
        if (!fallback) return undefined;
        const row = this.findReportRowForDistrict(cityAccessKey, district);
        if (!row) return undefined;
        return row.district?.accessKey?.trim() || fallback;
    }

    private reportHasCrop(crop: PlantationCropOption): boolean {
        const cropId = crop.id?.trim().toLowerCase();
        if (!cropId) return false;
        return this.reportRows.some((row) => row.cropId?.trim().toLowerCase() === cropId);
    }

    private reportHasCropDistrict(crop: PlantationCropOption, unitKey: string): boolean {
        const cropId = crop.id?.trim().toLowerCase();
        const normalizedUnitKey = unitKey.trim();
        if (!cropId || !normalizedUnitKey) return false;

        return this.reportRows.some((row) => {
            if (row.cropId?.trim().toLowerCase() !== cropId) {
                return false;
            }

            const fromReport = row.district?.accessKey?.trim();
            if (fromReport === normalizedUnitKey) {
                return true;
            }

            if (!this.params) {
                return false;
            }

            for (const city of this.params.cities) {
                const cityKey = city.cityId?.trim();
                if (!cityKey || row.city?.accessKey?.trim() !== cityKey) {
                    continue;
                }
                for (const district of city.districts) {
                    if (district.districtId?.trim() !== normalizedUnitKey) {
                        continue;
                    }
                    if (this.findReportRowForDistrict(cityKey, district) === row) {
                        return true;
                    }
                }
            }
            return false;
        });
    }

    private resolveDistrictNumericId(districtAccessKey: string): number | null {
        const fromFields = this.districtNumericIdByAccessKey.get(districtAccessKey);
        if (fromFields != null) return fromFields;
        const n = Number(districtAccessKey);
        return Number.isFinite(n) ? n : null;
    }

    setPanelOpen(open: boolean): void {
        this.panelOpenSubject.next(open);
    }

    /** Authorized district numeric ids from the current filter selection. */
    getSelectedDistrictIds(): number[] {
        if (!this.params) return [];
        const ids = new Set<number>();
        for (const city of this.params.cities) {
            const cityKey = city.cityId?.trim();
            if (!cityKey) continue;
            for (const district of city.districts) {
                const n = this.resolveDistrictNumericIdForDistrict(cityKey, district);
                if (n != null) ids.add(n);
            }
        }
        return [...ids];
    }

    private loadCityBoundariesForCountry(
        country: CropMapLocationCountryResponse | undefined,
        authorizedCityIds: string[],
    ): void {
        this.cancelCityBoundaryFetch$.next();
        this.cityBoundariesSubject.next(null);
        this.cityBoundariesLoadingSubject.next(true);

        this.cityBoundaryLoader
            .loadCityBoundariesForCountry(country)
            .pipe(
                takeUntil(this.cancelCityBoundaryFetch$),
                finalize(() => this.cityBoundariesLoadingSubject.next(false)),
            )
            .subscribe((geojson) => {
                this.cityBoundariesSubject.next({ geojson, authorizedCityIds });
            });
    }

    private loadDistrictBoundariesForCity(cityAccessKey: string, city: CityOption): void {
        this.cancelDistrictBoundaryFetch$.next();
        this.districtBoundariesSubject.next(null);
        this.districtBoundariesLoadingSubject.next(true);

        this.districtBoundaryLoader
            .loadDistrictBoundariesForCity(cityAccessKey)
            .pipe(
                takeUntil(this.cancelDistrictBoundaryFetch$),
                finalize(() => this.districtBoundariesLoadingSubject.next(false)),
            )
            .subscribe((geojson) => {
                const authorizedDistrictKeys = this.resolveDistrictBlobAccessKeys(city);
                this.districtBoundariesSubject.next({
                    cityAccessKey,
                    geojson,
                    authorizedDistrictKeys,
                });
            });
    }

    private clearDistrictBoundaries(): void {
        this.cancelDistrictBoundaryFetch$.next();
        this.districtBoundariesSubject.next(null);
    }

    private loadFieldBoundaries(
        params: PlantationParams,
        rows: CropAreaPlantationReportRowViewModel[],
        tile: FieldTile,
    ): void {
        this.cancelFieldBoundaryFetch$.next();

        if (params.crops.length === 0 || rows.length === 0) {
            console.warn('[PlantationService] Skipping field boundaries: missing crops, or report rows');
            return;
        }

        const { unitType } = tile;

        const unitKeys = this.collectFieldUnitAccessKeys(params, unitType);
        if (unitKeys.length === 0) {
            console.warn(
                `[PlantationService] Skipping field boundaries: no unit keys for unitType=${unitType}`,
            );
            return;
        }

        this.fieldBoundariesLoadingSubject.next(true);

        const seasonAccessKey = params.season.id;

        this.fieldBoundaryLoadService
            .loadMerged({
                year: params.year,
                seasonAccessKey,
                seasonName: params.season.name,
                crops: params.crops.map((c) => ({
                    id: c.id,
                    dataScienceId: c.dataScienceId,
                })),
                tile,
                unitKeys,
                cropAccessKeyFor: (crop) => {
                    const plCrop = params.crops.find((c) => c.id === crop.id);
                    return plCrop
                        ? this.resolveSeasonCropKeys(params, plCrop).cropAccessKey
                        : crop.id;
                },
                shouldFetchPair: (crop, unitKey, uType) => {
                    const plCrop = params.crops.find((c) => c.id === crop.id);
                    if (!plCrop) return false;
                    if (uType === 'country' && !this.reportHasCrop(plCrop)) {
                        return false;
                    }
                    if (uType === 'district' && !this.reportHasCropDistrict(plCrop, unitKey)) {
                        return false;
                    }
                    return true;
                },
            })
            .pipe(
                takeUntil(this.cancelFieldBoundaryFetch$),
                finalize(() => this.fieldBoundariesLoadingSubject.next(false)),
            )
            .subscribe((enriched) => {
                if (enriched.features.length > 0) {
                    this.loadedFieldTile = tile;
                    this.loadedFieldScopeKey = this.computeFieldScopeKey(tile);
                    if (unitType === 'district') {
                        for (const feature of enriched.features) {
                            const sampleDistrictId = feature.properties?.district_id;
                            if (typeof sampleDistrictId === 'number' && Number.isFinite(sampleDistrictId)) {
                                const unitKey = unitKeys.find((k) => k) ?? '';
                                if (unitKey) {
                                    this.districtNumericIdByAccessKey.set(unitKey, sampleDistrictId);
                                }
                                break;
                            }
                        }
                    }
                }
                this.fieldBoundariesSubject.next(enriched);
            });
    }

    private clearFieldBoundaries(): void {
        this.cancelFieldBoundaryFetch$.next();
        this.reportRows = [];
        this.loadedFieldTile = null;
        this.loadedFieldScopeKey = null;
        this.districtNumericIdByAccessKey.clear();
        this.fieldBoundariesSubject.next(null);
    }

    /** Geographic / report access keys for field blob `unitAccessKey` at the given unit level. */
    collectFieldUnitAccessKeys(params: PlantationParams, unitType: FieldUnitType): string[] {
        if (unitType === 'country') {
            const fromParams = params.country?.accessKey?.trim();
            if (fromParams) return [fromParams];

            for (const row of this.reportRows) {
                const fromReport = row.country?.accessKey?.trim();
                if (fromReport) return [fromReport];
            }

            const countryId = params.country?.countryId;
            if (countryId == null) return [];
            const fallback = String(countryId).trim();
            return fallback ? [fallback] : [];
        }

        if (this.mapScopedDistrictAccessKey) {
            return [this.mapScopedDistrictAccessKey];
        }

        let cities: CityOption[];
        if (this.mapScopedCityId) {
            cities = params.cities.filter((c) => c.cityId === this.mapScopedCityId);
        } else if (this.fieldViewportCityIds.length > 0) {
            const inViewport = new Set(this.fieldViewportCityIds);
            cities = params.cities.filter((c) => inViewport.has(c.cityId));
        } else {
            console.warn(
                '[PlantationService] Skipping district field keys: no mapScopedCityId or viewport cities',
            );
            return [];
        }
        const keys = new Set<string>();
        for (const city of cities) {
            for (const key of this.resolveDistrictBlobAccessKeys(city)) {
                if (key) keys.add(key);
            }
        }
        return [...keys];
    }

    /**
     * Stub: season/crop access keys for blob paths.
     * Replace when backend exposes dedicated access keys on season/crop models.
     */
    resolveSeasonCropKeys(params: PlantationParams, crop: PlantationCropOption): SeasonCropPathKeys {
        const seasonFromRow = this.reportRows[0]?.seasonId;
        const cropFromRow = this.reportRows.find((r) => r.cropId === crop.id)?.cropId;
        return {
            seasonAccessKey: seasonFromRow ?? params.season.id,
            cropAccessKey: cropFromRow ?? crop.id,
        };
    }

    /** Dev bin debug: report access key when loaded, else district id. */
    resolveDistrictBlobAccessKeyForDebug(city: CityOption, district: DistrictOption): string | undefined {
        const cityKey = city.cityId?.trim();
        const fallback = district.districtId?.trim();
        if (!cityKey || !fallback) {
            return undefined;
        }
        const row = this.findReportRowForDistrict(cityKey, district);
        return row?.district?.accessKey?.trim() || fallback;
    }

    /** Dev bin debug: `{AppGeographicBoundariesPath}/countries/{AccessKey}.bin` and regions file key. */
    resolveCountryAccessKeyForDebug(): string | undefined {
        if (!this.params) {
            return undefined;
        }
        return this.collectFieldUnitAccessKeys(this.params, 'country')[0];
    }

    /** Dev bin debug: `{AppGeographicBoundariesPath}/cities/{RegionAccessKey}.bin`. */
    resolveRegionAccessKeyForDebug(city: CityOption, district?: DistrictOption): string | undefined {
        const cityKey = city.cityId?.trim();
        if (!cityKey) {
            return undefined;
        }
        const row = district
            ? this.findReportRowForDistrict(cityKey, district)
            : this.reportRows.find((r) => r.city?.accessKey?.trim() === cityKey);
        const fromReport = row?.regionOrState?.accessKey?.trim();
        if (fromReport) {
            return fromReport;
        }
        if (!this.params) {
            return undefined;
        }
        for (const region of this.params.regionOrStates) {
            if (region.cities.some((c) => c.cityId === cityKey)) {
                const key = region.id?.trim();
                return key || undefined;
            }
        }
        return undefined;
    }

    /** Dev bin debug: city access key for location districts blob and plantation city unit. */
    resolveCityBlobAccessKeyForDebug(city: CityOption, district?: DistrictOption): string | undefined {
        const cityKey = city.cityId?.trim();
        if (!cityKey) {
            return undefined;
        }
        const row = district
            ? this.findReportRowForDistrict(cityKey, district)
            : this.reportRows.find((r) => r.city?.accessKey?.trim() === cityKey);
        return row?.city?.accessKey?.trim() || cityKey;
    }

    /** Dev bin debug: plantation `region` unit access key from report or params. */
    resolveRegionPlantationUnitAccessKeyForDebug(city: CityOption, district?: DistrictOption): string | undefined {
        return this.resolveRegionAccessKeyForDebug(city, district);
    }

    buildFieldPath(
        basePath: string,
        year: number,
        seasonAccessKey: string,
        cropAccessKey: string,
        unitType: string,
        unitAccessKey: string,
        zoom: number,
    ): string {
        return this.fieldBoundaryLoadService.buildFieldPath(
            basePath,
            year,
            seasonAccessKey,
            cropAccessKey,
            unitType,
            unitAccessKey,
            zoom,
        );
    }

    /** Clears session-scoped plantation map and report state (logout / account switch). */
    resetOnLogout(): void {
        this.cancelCityBoundaryFetch$.next();
        this.cancelDistrictBoundaryFetch$.next();
        this.cancelFieldBoundaryFetch$.next();

        this.params = null;
        this.mapDrillBaseParams = null;
        this.mapScopedCityId = null;
        this.mapScopedDistrictAccessKey = null;
        this.mapDrillSourceCity = null;
        this.mapDrillDistrictBlobKeys.clear();
        this.fieldViewportCityIds = [];
        this.loadedFieldScopeKey = null;
        this.loadedFieldTile = null;
        this.currentCountryId = null;
        this.fieldMapZoom = 1;
        this.highlightedCity = null;

        this.paramsSubject.next(null);
        this.dataSubject.next([]);
        this.loadingSubject.next(false);
        this.highlightedCitySubject.next(null);
        this.panelOpenSubject.next(true);
        this.cityBoundariesSubject.next(null);
        this.cityBoundariesLoadingSubject.next(false);
        this.clearDistrictBoundaries();
        this.clearFieldBoundaries();
    }
}
